import { motion } from "framer-motion";
import { ShieldCheck, UserCheck, RefreshCcw, HeartPulse } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Background-verified staff",
    desc: "Every caretaker and nurse passes ID, address and reference verification before their first deployment.",
  },
  {
    icon: UserCheck,
    title: "Qualified professionals",
    desc: "Nursing roles are filled only by GNM and B.Sc graduates with verified Nursing Council registration.",
  },
  {
    icon: RefreshCcw,
    title: "Free replacement guarantee",
    desc: "If a caretaker or nurse isn't the right fit, we replace them at no extra cost — no questions asked.",
  },
  {
    icon: HeartPulse,
    title: "Care supervised by coordinators",
    desc: "A dedicated care coordinator checks in on every case — vitals, schedules and family updates.",
  },
];

const STATS = [
  { value: "24/7", label: "Care coordination" },
  { value: "45 min", label: "Avg. response time" },
  { value: "100%", label: "Verified staff" },
  { value: "Same-day", label: "Deployment" },
];

const WhySharpin = () => (
  <section id="why" className="py-16 md:py-24 bg-white border-y border-slate-200" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <SectionHeading
        number="04 — Why Sharpin"
        title="Care you can trust,"
        accent="standards you can check."
        subtitle="We hold every deployment to hospital-grade standards — because the person receiving care is someone's family."
        id="why"
      />

      <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 mb-20">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, x: i % 2 === 0 ? -32 : 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="flex gap-5"
            data-testid={`why-feature-${i}`}
          >
            <div className="shrink-0 h-14 w-14 rounded-2xl bg-tealbrand-light flex items-center justify-center">
              <f.icon className="h-6 w-6 text-tealbrand-dark" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-navy mb-2">{f.title}</h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="bg-navy rounded-3xl px-8 py-12 md:px-16 grid grid-cols-2 lg:grid-cols-4 gap-10"
        data-testid="why-stats-band"
      >
        {STATS.map((s) => (
          <div key={s.label}>
            <p className="font-display text-3xl md:text-4xl font-extrabold text-tealbrand-light">{s.value}</p>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default WhySharpin;
